import { fetchClient } from "../utils/fetchClient";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:8000/api";

export interface User {
  id: number;
  username: string;
  email: string;
  is_superuser: boolean;
  is_staff: boolean;
  is_active: boolean;
  date_joined?: string;
}

export interface UserPaginationInfo {
  page: number;
  page_size: number;
  total_items: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
}

export interface UsersResponse {
  users: User[];
  pagination: UserPaginationInfo;
}

export interface CreateUserData {
  username: string;
  email: string;
  password: string;
  confirm_password: string;
}

export interface UpdateUserData {
  username?: string;
  email?: string;
  is_active?: boolean;
  is_staff?: boolean;
  is_superuser?: boolean;
}

class UserService {
  private getAuthHeaders(token: string) {
    const tid = localStorage.getItem("tenantId");
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(tid ? { "X-Tenant-Id": tid } : {}),
    };
  }

  // READ - Get all users with pagination
  async getUsers(
    token: string,
    page: number = 1,
    pageSize: number = 20,
    searchQuery: string = "",
  ): Promise<UsersResponse> {
    try {
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("page_size", pageSize.toString());
      if (searchQuery) {
        params.append("search", searchQuery);
      }

      const response = await fetchClient(
        `${API_BASE_URL}/users/?${params.toString()}`,
        {
          headers: this.getAuthHeaders(token),
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch users: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching users:", error);
      throw error;
    }
  }

  // CREATE - Add new user
  async createUser(token: string, userData: CreateUserData): Promise<User> {
    try {
      const response = await fetchClient(`${API_BASE_URL}/users/create/`, {
        method: "POST",
        headers: this.getAuthHeaders(token),
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to create user");
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error("Error creating user:", error);
      throw error;
    }
  }

  // UPDATE - Edit user
  async updateUser(
    token: string,
    userId: number,
    userData: UpdateUserData,
  ): Promise<User> {
    try {
      const response = await fetchClient(`${API_BASE_URL}/users/${userId}/`, {
        method: "PUT",
        headers: this.getAuthHeaders(token),
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update user");
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error("Error updating user:", error);
      throw error;
    }
  }

  // DELETE - Remove user
  async deleteUser(token: string, userId: number): Promise<void> {
    try {
      const response = await fetchClient(
        `${API_BASE_URL}/users/${userId}/delete/`,
        {
          method: "DELETE",
          headers: this.getAuthHeaders(token),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete user");
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      throw error;
    }
  }
}

export const userService = new UserService();
